import { FontAwesome5 } from '@expo/vector-icons';
import {
  flex,
  Loading,
  ml,
  OEText,
  PrismicRichText,
} from '@openeducation/oe-rn-components';
import { styled } from '@openeducation/oe-rn-themes';
import React, { useRef, useState } from 'react';
import { LayoutChangeEvent, StyleProp, View } from 'react-native';
import type { ViewStyle } from 'react-native';
import { useHover } from 'react-native-web-hooks';

import { useLessonEvaluateDescription } from './use-lesson-evaluate-description';

type LessonEvaluateTooltipProps = {
  style?: StyleProp<ViewStyle>;
};

const LessonEvaluateTooltip = ({ style }: LessonEvaluateTooltipProps) => {
  const linkRef = useRef(null);
  const isLinkHovered = useHover(linkRef);
  const [tooltipHeight, setTooltipHeight] = useState(350);
  const { description, isLoading: isDescriptionLoading } =
    useLessonEvaluateDescription();

  const renderTooltipContent = () => {
    if (isDescriptionLoading) {
      return (
        <View
          style={[
            flex('align-items-center', 'justify-content-center', 'flex-1'),
          ]}
        >
          <Loading size="large" />
        </View>
      );
    }

    return <PrismicRichText field={description?.data.message} />;
  };

  const tooltipOnLayout = (event: LayoutChangeEvent) => {
    const height = event?.nativeEvent.layout.height;
    height && setTooltipHeight(height);
  };

  const renderTooltip = () => (
    <Tooltip
      onLayout={tooltipOnLayout}
      pointerEvents="none"
      isLinkHovered={isLinkHovered}
      tooltipHeight={tooltipHeight}
    >
      {renderTooltipContent()}
      <TriangleUp />
    </Tooltip>
  );

  return (
    <View style={style}>
      <View style={[flex('flex-row', 'align-items-center')]} ref={linkRef}>
        <UnderlineText fontType="italic" fontSize={12}>
          How to evaluate?
        </UnderlineText>

        <View style={[ml(4)]}>
          <QuestionMarkIcon />
        </View>
      </View>
      {renderTooltip()}
    </View>
  );
};

const UnderlineText = styled(OEText)`
  text-decoration: underline;
`;

const QuestionMarkIcon = styled(FontAwesome5).attrs(({ theme }) => {
  return {
    color: theme.color.dark1,
    name: 'question-circle',
    size: 14,
    solid: true,
  };
})``;

type TooltipType = {
  tooltipHeight: number;
  isLinkHovered: boolean;
};

const Tooltip = styled.View<TooltipType>`
  width: 330px;
  padding: 24px;
  background-color: ${({ theme }) => theme.color.white};
  border-radius: 8px;
  position: absolute;
  bottom: ${({ tooltipHeight }) => -tooltipHeight - 15}px;
  opacity: ${({ isLinkHovered }) => (isLinkHovered ? 1 : 0)};
  right: -20px;
  shadow-opacity: 0.1;
  shadow-radius: 16px;
  elevation: 5;
`;

const TriangleUp = styled.View`
  width: 0;
  height: 0;
  background-color: transparent;
  border-left-width: 8px;
  border-right-width: 8px;
  border-bottom-width: 10px;
  border-left-color: transparent;
  border-right-color: transparent;
  border-bottom-color: ${({ theme }) => theme.color.white};
  position: absolute;
  top: -10px;
  right: 20px;
  z-index: 2;
`;

export default LessonEvaluateTooltip;
